import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { ExchangeType, IConnectionsReport, IConnectionsSearchParams, User } from "../Interfaces/connections";
import {
  IApplyOptionConfig,
  IApplyOptionInsMarginConfig,
  IApplyOptionProgress,
  IApplyOptionsCommonSearchParam,
  IApplyPlanTableReport,
  IConflictTableReport,
  IOmsResultTableReport,
  ISubmitRequstsTableRequests,
  ISubmitRequstsTableResponse,
  IUpdatePortfoStatus,
  ProgressType,
} from "../Interfaces/applyOption";
import { Packet } from "../Interfaces/commonDtos";
import { ITableReport, Report } from "../Interfaces/TableInterfaces";
import {
  downloadExcel,
  getRequest,
  getTable,
  getTableWithExtraData,
  getTableWithGet,
  postDownloadExcel,
  postRequest,
  putRequest,
} from "./requestTemplates";

export const getProgress = (): Promise<IApplyOptionProgress[]> =>
  getRequest("/v1/ApplyOption/GetProgress");

export const getApplyOptionBrokers = (): Promise<{ id: string; name: string }[]> =>
  getRequest("/v1/ApplyOption/GetBrokers");

export const getApplyOptionUsers = async (): Promise<User[]> => {
  const result = await getTable<IConnectionsSearchParams, IConnectionsReport>(
    "/v1/connection/table",
    { exchangeType: ExchangeType.StockExchange },
    0,
    1000,
  );
  return result.report.map(({ id, name, connectionStatus }) => ({
    id,
    name,
    connectionStatus,
  }));
};

export const getApplyOptionConfig = (): Promise<IApplyOptionConfig> =>
  getRequest("/v1/ApplyOption/GetConfigs");

export const setApplyOptionConfig = (
  dto: IApplyOptionConfig,
): Promise<Packet<boolean>> => putRequest("/v1/ApplyOption/setConfigs", dto);

export const getApplyOptionInstrumentMarginConfig =
  (): Promise<IApplyOptionInsMarginConfig> =>
    getRequest("/v1/ApplyOption/GetInstrumentMarginConfigs");

export const setApplyOptionInstrumentMarginConfig = (
  dto: IApplyOptionInsMarginConfig,
): Promise<Packet<boolean>> =>
  putRequest("/v1/ApplyOption/SetInstrumentMarginConfig", dto);

export const getQueueStatusTable = async (
  searchParam: {},
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTableWithGet<{}, any>(
    "/v1/ApplyOption/GetQueueStatusTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getSubmitRequestsTable = async (
  searchParam: IApplyOptionsCommonSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<ISubmitRequstsTableResponse> =>
  getTableWithExtraData<
    IApplyOptionsCommonSearchParam,
    ISubmitRequstsTableRequests,
    ISubmitRequstsTableResponse
  >("/v1/ApplyOption/SubmitRequests", searchParam, offset, pageSize, sortingType);

export const updatePortfoStatus = (): Promise<Packet<boolean>> =>
  postRequest("/v1/ApplyOption/UpdatePortfos", {});

export const updateTradingBooksStatus = (): Promise<Packet<boolean>> =>
  postRequest("/v1/ApplyOption/UpdateTradingBooks", {});

export const getProgressByType = (
  type: ProgressType,
): Promise<IApplyOptionProgress> =>
  getRequest(
    `/v1/ApplyOption/GetProgressByType?applyOptionProgressType=${type}`,
  );

export const submitApplyRemainingOptionRequest = (): Promise<Packet<boolean>> =>
  postRequest("/v1/ApplyOption/SubmitApplyRemainingOptionRequest", {});

export const traceAppliedRemainingOptionRequest = (): Promise<
  Packet<boolean>
> => postRequest("/v1/ApplyOption/TraceAppliedRemainingOptionRequest", {});

export const getSubmitRequestsExcelFile = (
  searchParam: IApplyOptionsCommonSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/ApplyOption/SubmitRequestsExcel?${rand}`,
    searchParam,
  );
};
export const getErrorTableExcelFile = (
  searchParam: IApplyOptionsCommonSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/ApplyOption/ErrorTableExcel?${rand}`,
    searchParam,
  );
};

export const getApplyPlanTableExcelFile = (
  searchParam: IApplyOptionsCommonSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/ApplyOption/GetApplyPlanExcel?${rand}`,
    searchParam,
  );
};

export const getErrorTable = async (
  searchParam: IApplyOptionsCommonSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable<IApplyOptionsCommonSearchParam, any>(
    "/v1/ApplyOption/ApplyOptionErrors",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );
export const getLastTradingBookExcelFile = (
  searchParams: IApplyOptionsCommonSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/ApplyOption/LastTradingBookExcel?${rand}`,
    searchParams,
  );
};

export const getLastTradingBook = async (
  searchParam: IApplyOptionsCommonSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable<IApplyOptionsCommonSearchParam, any>(
    "/v1/ApplyOption/LastTradingBook",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getApplyPlanTable = async (
  searchParam: IApplyOptionsCommonSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IApplyPlanTableReport>> =>
  getTable<IApplyOptionsCommonSearchParam, IApplyPlanTableReport>(
    "/v1/ApplyOption/getApplyPlanTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getAccountOptionExcelFile = (
  searchParam: IApplyOptionsCommonSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/ApplyOption/AccountOptionExcel?${rand}`,
    searchParam,
  );
};

export const getPortfolioBuyingPosition = async (
  searchParam: IApplyOptionsCommonSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable<IApplyOptionsCommonSearchParam, any>(
    "/v1/ApplyOption/GetPortfolioBuyingPosition",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getUpdatePortfoStatus = (): Promise<IUpdatePortfoStatus> =>
  getRequest("/v1/ApplyOption/GetUpdatePortfoStatus");

export const getApplyOptionPlanStatusDropdown = (): Promise<DropdownItem[]> =>
  getRequest("/v1/ApplyOption/ApplyOptionPlanStatusDropdown");

export const manualSendPlanRow = (id: number): Promise<Packet<boolean>> =>
  postRequest(`/v1/ApplyOption/ManualSendPlanRow/${id}`, {});

export const manualSendPlanRowBulk = (ids: number[]): Promise<Packet<boolean>> =>
  postRequest(`/v1/ApplyOption/ManualSendPlanRowBulk`, ids);

export const getConflictTable = async (
  searchParam: IApplyOptionsCommonSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IConflictTableReport>> =>
  getTable<IApplyOptionsCommonSearchParam, IConflictTableReport>(
    "/v1/ApplyOption/GetConflictTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getOmsResultTable = async (
  searchParam: IApplyOptionsCommonSearchParam,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<IOmsResultTableReport>> =>
  getTable<IApplyOptionsCommonSearchParam, IOmsResultTableReport>(
    "/v1/ApplyOption/GetOmsResultTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getConflictTableExcelFile = (
  searchParam: IApplyOptionsCommonSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/ApplyOption/ConflictTableExcel?${rand}`,
    searchParam,
  );
};

export const getOmsResultTableExcelFile = (
  searchParam: IApplyOptionsCommonSearchParam,
) => {
  const rand = Math.random();
  return postDownloadExcel(
    `/v1/ApplyOption/OmsResultTableExcel?${rand}`,
    searchParam,
  );
};
